import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useSupportEmail -- shared read of the Super Admin ``support-email`` platform
 * setting (card #573, Jake 2026-10-29): the address customers are told to
 * write to for help, e.g. "For help with art cleanup, email <address>."
 *
 * Fail-SAFE to the DEFAULT address: ``supportEmail`` reads the default while
 * the fetch is in flight, if it errors, and if the payload is not one plain
 * email address. It is never blank and never a half-formed value, because it
 * is shown to customers and used as a ``mailto:`` target. (The server
 * validates on write with the same shape; this is the belt to its braces.)
 *
 * One GET per page load: module-level cache + in-flight dedupe.
 *
 *   const { supportEmail } = useSupportEmail()
 */
export const DEFAULT_SUPPORT_EMAIL = 'support@threadflow.com'

const ENDPOINT = '/api/platform-settings/support-email'

// Mirrors the server allowlist (platform_settings.plain_email_address_or_none):
// one ASCII address, dot-separated atoms, a real domain with a TLD.
const EMAIL_RE =
    /^[A-Za-z0-9_+-]+(?:\.[A-Za-z0-9_+-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/

function plainEmailOrNull(raw) {
    if (typeof raw !== 'string') return null
    if (raw.length > 254) return null
    const local = raw.split('@')[0]
    if (local.length > 64) return null
    return EMAIL_RE.test(raw) ? raw : null
}

const _value = ref(DEFAULT_SUPPORT_EMAIL)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _fetchOnce() {
    if (_resolved) return Promise.resolve(_value.value)
    if (_inflight) return _inflight
    _error.value = null
    // The leading .then also turns a synchronous throw from the client into
    // the same fail-safe path as a rejected request.
    _inflight = Promise.resolve()
        .then(() => axiosInstance.get(ENDPOINT))
        .then((resp) => {
            const served = plainEmailOrNull(resp && resp.data ? resp.data.value : null)
            _value.value = served || DEFAULT_SUPPORT_EMAIL
            _resolved = true
            return _value.value
        })
        .catch((err) => {
            // Fail SAFE; not marked resolved so a later mount retries.
            _error.value = err
            _value.value = DEFAULT_SUPPORT_EMAIL
            return _value.value
        })
        .finally(() => {
            _inflight = null
        })
    return _inflight
}

/** Test-only: reset the module-level shared state. */
export function _resetSupportEmailCache() {
    _value.value = DEFAULT_SUPPORT_EMAIL
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useSupportEmail() {
    _fetchOnce()
    return { supportEmail: _value, error: _error }
}

export default useSupportEmail
